import { useEffect, useRef, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { signOut, useSession } from 'next-auth/react';
import { usePostHog } from 'posthog-js/react';

import { Features } from '@lessonPlanner/constants';
import { generateInitials } from '@lessonPlanner/string';

import { routes } from '../constants/routes';
import {
  checkInAppBrowser,
  useInAppBrowser,
} from '../hooks/useCheckInAppBrowser';
import { useModalState } from '../hooks/useModalState';
import { signInUser } from '../utils/auth.utils';
import { getIsResponsePopulated } from '../utils/responsePopulatedUtil';
import { UserCreditType } from '../utils/server-side/credits';

import ModalAlerts from './organisms/Modal/ModalAlerts';
import PopUpModal from './PopUpModal';
import PremiumIcon from './PremiumIcon';
import { SignInBtn } from './SigninBtn';

interface UserMenuProps {
  creditsData: UserCreditType;
}

export function UserMenu({ creditsData }: UserMenuProps) {
  const [shouldShowDropdown, setShouldShowDropdown] = useState(false);
  const { data: session } = useSession();
  const router = useRouter();
  const user = session?.user;
  const posthog = usePostHog();
  const isUIRevampEnabled = posthog.isFeatureEnabled(Features.UI_REVAMP);

  const [initials, setInitials] = useState<string>('');
  const dropdownRef = useRef(null);

  const { popUpData, setPopUpData } = useInAppBrowser();

  const [confirmationCallback, setConfirmationCallback] = useState<
    (() => void) | null
  >(null);
  const [modalRoute, setModalRoute] = useState<string | null>(null);

  useEffect(() => {
    if (user?.name) {
      const userInitials = generateInitials(user.name);
      setInitials(userInitials);
    }
  }, [user?.name]);

  const handleSignIn = () => {
    if (checkInAppBrowser()) {
      setPopUpData((prev) => ({ ...prev, isOpen: true }));
      return;
    }
    const { query } = router;
    const queryString = new URLSearchParams(
      query as Record<string, string>,
    ).toString();

    signInUser({
      callbackUrl: `${router.pathname}?${queryString}`,
    });
  };

  const handleSignOut = () => {
    const signOutRedirectUrl = '/lesson-plan';
    posthog.reset();
    signOut({
      callbackUrl: signOutRedirectUrl,
      redirect: isUIRevampEnabled,
    });
  };

  const handleRedirect = () => {
    if (!getIsResponsePopulated()) {
      handleSignOut();
    } else {
      openModal('/lesson-plan', handleSignOut);
    }
  };

  const toggleDropdown = () => {
    setShouldShowDropdown(!shouldShowDropdown);
  };

  const { setShowConfirmationModal, showConfirmationModal } = useModalState({});

  const handleConfirm = () => {
    if (confirmationCallback) {
      confirmationCallback();
      setConfirmationCallback(null);
      if (modalRoute) router.push(modalRoute);
    }
    setShowConfirmationModal(false);
  };

  function openModal(route: string, onConfirm: () => void) {
    if (getIsResponsePopulated() && !showConfirmationModal) {
      setModalRoute(route);
      setShowConfirmationModal(true);
      setConfirmationCallback(() => onConfirm);
    }
  }

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShouldShowDropdown(false);
      }
    }

    if (shouldShowDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [shouldShowDropdown]);

  return session ? (
    <div
      ref={dropdownRef}
      className="relative rounded-full bg-blue-100 px-1 py-1 font-semibold text-blue-800 shadow hover:bg-blue-200"
    >
      <div
        className="flex h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-blue-500"
        onClick={toggleDropdown}
      >
        <span className="select-none text-lg font-semibold text-white">
          {initials}
        </span>
      </div>
      {shouldShowDropdown && (
        <div className="absolute right-0 z-10 mt-1 w-52 rounded-md bg-white pb-1 shadow-lg">
          <div className="border-b px-4 py-3 text-sm">
            <p className="font-medium text-gray-900">{user.name}</p>
            <small className="text-gray-600">{user.email}</small>
            <div className="mt-3 flex flex-col items-start sm:hidden">
              <div className="flex items-center gap-2">
                <div className="text- flex items-center justify-center gap-[2px] leading-none">
                  <Image
                    alt="stars"
                    height={10}
                    src="/images/blue-stars.svg"
                    width={10}
                  />
                  <p className="font-semibold">
                    {creditsData?.credits || 0} Regular
                  </p>
                </div>
                <div className="flex items-center justify-center text-sm leading-none">
                  <PremiumIcon className="w-4 fill-[#FFB743]" />
                  <p className="font-semibold">
                    {creditsData?.paidCredits || 0} Premium
                  </p>
                </div>
              </div>
              <Link
                className="my-0.5 w-full whitespace-nowrap rounded-full bg-blue-100 px-2.5 py-0.5 text-center text-sm text-blue-700 hover:bg-blue-200 hover:text-blue-700"
                href={routes.PRICING}
                rel="noopener noreferrer"
                target="_blank"
              >
                Top up
              </Link>
            </div>
          </div>
          <div>
            <button
              className="w-full px-4 py-2 text-start text-sm font-medium leading-5 text-gray-900 transition duration-150 ease-in-out hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              onClick={handleRedirect}
            >
              Sign Out
            </button>
          </div>
        </div>
      )}
      <ModalAlerts
        handleConfirmClose={handleConfirm}
        setShowConfirmationModal={setShowConfirmationModal}
        showConfirmationModal={showConfirmationModal}
      />
    </div>
  ) : (
    <>
      <SignInBtn handleSignIn={handleSignIn} />
      <PopUpModal {...popUpData} setIsOpen={setPopUpData} />
    </>
  );
}
